import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { ArrowLeft, Save, Trash2, Upload, ImagePlus, X, ImageOff } from "lucide-react";

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function AdminRecipeDetail() {
  const { recipeId } = useParams();
  const nav = useNavigate();
  const [r, setR] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(`/admin/recipes/${recipeId}`).then(({ data }) => setR({
      ...data,
      _ingredients: (data.ingredients || []).join("\n"),
      _instructions: (data.instructions || []).join("\n"),
      _tags: (data.tags || []).join(", "),
    }));
  }, [recipeId]);

  if (!r) return <div className="font-serif-italic text-[hsl(var(--brand-muted))]">Loading…</div>;

  const save = async () => {
    const payload = {
      name: r.name,
      description: r.description || "",
      yield_str: r.yield_str || "",
      image_url: r.image_url || "",
      prep_time_minutes: r.prep_time_minutes || null,
      total_time_minutes: r.total_time_minutes || null,
      ingredients: r._ingredients.split("\n").map((s) => s.trim()).filter(Boolean),
      instructions: r._instructions.split("\n").map((s) => s.trim()).filter(Boolean),
      tags: r._tags.split(",").map((s) => s.trim()).filter(Boolean),
      notes: r.notes || "",
    };
    await api.put(`/admin/recipes/${recipeId}`, payload);
    toast.success("Recipe saved");
  };
  const del = async () => {
    if (!window.confirm("Delete this recipe?")) return;
    await api.delete(`/admin/recipes/${recipeId}`);
    toast.success("Deleted");
    nav("/admin/recipes");
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/admin/recipes/${recipeId}/image`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setR({ ...r, image_url: data.image_url });
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async () => {
    if (!window.confirm("Remove photo?")) return;
    await api.delete(`/admin/recipes/${recipeId}/image`);
    setR({ ...r, image_url: "" });
    toast.success("Photo removed");
  };

  const imgSrc = resolveImageUrl(r.image_url);

  return (
    <div data-testid="admin-recipe-detail-page">
      <Link to="/admin/recipes" className="text-sm text-[hsl(var(--brand-muted))] inline-flex items-center gap-1 hover:text-[hsl(var(--brand-ink))]">
        <ArrowLeft className="w-3 h-3" /> All recipes
      </Link>
      <div className="grid md:grid-cols-[1fr_1.4fr] gap-8 mt-6">
        <div>
          <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[hsl(var(--brand-cream-deep))] border border-[hsl(var(--brand-border))] relative">
            {imgSrc ? (
              <img src={imgSrc} className="w-full h-full object-cover" alt={r.name} />
            ) : (
              <div className="w-full h-full grid place-items-center text-[hsl(var(--brand-muted))]">
                <div className="text-center">
                  <ImageOff className="w-10 h-10 mx-auto opacity-40" />
                  <div className="text-xs mt-2">No photo yet</div>
                </div>
              </div>
            )}
            {imgSrc ? (
              <button onClick={removePhoto} className="absolute top-2 right-2 bg-white/90 hover:bg-white rounded-full p-2 text-[hsl(var(--destructive))] shadow-sm" data-testid="remove-photo-btn" title="Remove photo">
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onPickFile} className="hidden" data-testid="recipe-photo-input" />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))] disabled:opacity-60"
            data-testid="upload-photo-btn"
          >
            {imgSrc ? <ImagePlus className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : imgSrc ? "Change photo" : "Upload photo"}
          </button>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Prep (min)</label>
              <input type="number" value={r.prep_time_minutes || ""} onChange={(e) => setR({ ...r, prep_time_minutes: parseInt(e.target.value) || null })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="recipe-prep-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Yield</label>
              <input value={r.yield_str || ""} onChange={(e) => setR({ ...r, yield_str: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="recipe-yield-input" />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-xs uppercase tracking-[0.2em] text-[hsl(var(--brand-muted))] mb-1">Tags (comma separated)</label>
            <input value={r._tags} onChange={(e) => setR({ ...r, _tags: e.target.value })} className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md" data-testid="recipe-tags-input" />
          </div>
          <div className="mt-8 flex gap-2">
            <button onClick={save} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[hsl(var(--brand-sage))] text-white hover:bg-[hsl(var(--brand-sage-hover))]" data-testid="save-recipe-btn">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={del} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm text-[hsl(var(--destructive))] hover:bg-red-50" data-testid="delete-recipe-btn">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          </div>
        </div>
        <div>
          <input
            value={r.name}
            onChange={(e) => setR({ ...r, name: e.target.value })}
            className="w-full font-display text-4xl md:text-5xl leading-tight tracking-tight bg-transparent border-b border-[hsl(var(--brand-border))] focus:outline-none focus:border-[hsl(var(--brand-sage))] py-2"
            data-testid="recipe-name-input"
          />
          <textarea
            rows={2}
            value={r.description || ""}
            onChange={(e) => setR({ ...r, description: e.target.value })}
            placeholder="Short description…"
            className="mt-4 w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md text-[hsl(var(--brand-muted))] font-serif-italic"
            data-testid="recipe-desc-input"
          />
          <div className="mt-8">
            <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))] mb-2">Ingredients</div>
            <textarea
              rows={10}
              value={r._ingredients}
              onChange={(e) => setR({ ...r, _ingredients: e.target.value })}
              className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md font-mono text-sm"
              data-testid="recipe-ingredients-textarea"
            />
          </div>
          <div className="mt-8">
            <div className="text-xs tracking-[0.3em] uppercase text-[hsl(var(--brand-terracotta))] mb-2">Instructions</div>
            <textarea
              rows={10}
              value={r._instructions}
              onChange={(e) => setR({ ...r, _instructions: e.target.value })}
              className="w-full px-3 py-2 border border-[hsl(var(--brand-border))] rounded-md text-sm"
              data-testid="recipe-instructions-textarea"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
